/**
 * Logo Crop Dialog Component
 * Provides image cropping interface using react-image-crop
 */

import { useState, useRef, useEffect } from 'react';
import ReactCrop, { type Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/core/components/ui/dialog';
import { Button } from '@/core/components/ui/button';
import { CropArea } from '../../../kiosk/types/kiosk-types';

interface LogoCropDialogProps {
  open: boolean;
  imageUrl: string;
  initialCropArea?: CropArea;
  onConfirm: (croppedBlob: Blob, cropArea: CropArea) => void;
  onCancel: () => void;
  title?: string;
}

/**
 * Create a cropped image blob from the original image and crop area
 * Scales up small crops to ensure minimum 400px on longest side for quality display
 */
async function getCroppedImg(
  image: HTMLImageElement,
  pixelCrop: PixelCrop
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Calculate the scale ratio between displayed image and natural image size
  // pixelCrop coordinates are based on the displayed size, we need natural size
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  // Convert crop coordinates from displayed size to natural size
  const sourceX = pixelCrop.x * scaleX;
  const sourceY = pixelCrop.y * scaleY;
  const sourceWidth = pixelCrop.width * scaleX;
  const sourceHeight = pixelCrop.height * scaleY;

  // Calculate scale factor to ensure minimum 400px on longest side
  const MIN_DIMENSION = 400;
  const longestSide = Math.max(sourceWidth, sourceHeight);
  const scaleFactor = longestSide < MIN_DIMENSION ? MIN_DIMENSION / longestSide : 1;

  // Set canvas size with scaling applied
  canvas.width = sourceWidth * scaleFactor;
  canvas.height = sourceHeight * scaleFactor;

  // Enable image smoothing for better quality when scaling up
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Draw the cropped and scaled image using natural dimensions
  ctx.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    canvas.width,
    canvas.height
  );

  // Convert canvas to blob with high quality
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob from canvas'));
        }
      },
      'image/png',
      1.0 // Maximum quality
    );
  });
}

export function LogoCropDialog({
  open,
  imageUrl,
  initialCropArea,
  onConfirm,
  onCancel,
  title = 'Crop Logo',
}: LogoCropDialogProps) {
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 100,
    height: 100,
    x: 0,
    y: 0,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Set initial crop when dialog opens and image is loaded
  useEffect(() => {
    if (!open) return;

    const handleImageLoad = () => {
      if (!imgRef.current) return;

      if (initialCropArea) {
        // initialCropArea is in natural image coordinates (from previous crop)
        // Convert to percentage of natural dimensions for display
        const percentCrop: Crop = {
          unit: '%',
          x: (initialCropArea.x / imgRef.current.naturalWidth) * 100,
          y: (initialCropArea.y / imgRef.current.naturalHeight) * 100,
          width: (initialCropArea.width / imgRef.current.naturalWidth) * 100,
          height: (initialCropArea.height / imgRef.current.naturalHeight) * 100,
        };
        setCrop(percentCrop);

        // Convert natural coordinates to displayed coordinates for completedCrop
        const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
        const scaleY = imgRef.current.naturalHeight / imgRef.current.height;

        const pixelCrop: PixelCrop = {
          x: initialCropArea.x / scaleX,
          y: initialCropArea.y / scaleY,
          width: initialCropArea.width / scaleX,
          height: initialCropArea.height / scaleY,
          unit: 'px',
        };
        setCompletedCrop(pixelCrop);
      } else {
        // No existing crop - default to full image (100%)
        setCrop({
          unit: '%',
          width: 100,
          height: 100,
          x: 0,
          y: 0,
        });

        // Set completedCrop in DISPLAYED coordinates (not natural)
        const pixelCrop: PixelCrop = {
          x: 0,
          y: 0,
          width: imgRef.current.width,  // Use displayed width, not natural
          height: imgRef.current.height,  // Use displayed height, not natural
          unit: 'px',
        };
        setCompletedCrop(pixelCrop);
      }
    };

    // Use a small timeout to ensure the dialog and image are fully rendered
    const timeoutId = setTimeout(() => {
      const img = imgRef.current;
      if (img) {
        if (img.complete && img.naturalWidth > 0) {
          // Image already loaded
          handleImageLoad();
        } else {
          // Wait for image to load
          img.addEventListener('load', handleImageLoad);
        }
      }
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      const img = imgRef.current;
      if (img) {
        img.removeEventListener('load', handleImageLoad);
      }
    };
  }, [open, initialCropArea, imageUrl]);

  const handleUseFullImage = () => {
    const fullCrop: Crop = {
      unit: '%',
      width: 100,
      height: 100,
      x: 0,
      y: 0,
    };
    setCrop(fullCrop);

    // Calculate pixel crop immediately for full image (in DISPLAYED coordinates)
    if (imgRef.current) {
      const pixelCrop: PixelCrop = {
        x: 0,
        y: 0,
        width: imgRef.current.width,  // Use displayed width, not natural
        height: imgRef.current.height,  // Use displayed height, not natural
        unit: 'px',
      };
      setCompletedCrop(pixelCrop);
    }
  };

  const handleConfirm = async () => {
    if (!completedCrop || !imgRef.current) return;

    setIsProcessing(true);
    try {
      const croppedBlob = await getCroppedImg(imgRef.current, completedCrop);

      // Convert completedCrop (in displayed coordinates) to natural coordinates for storage
      const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
      const scaleY = imgRef.current.naturalHeight / imgRef.current.height;

      const cropArea: CropArea = {
        x: completedCrop.x * scaleX,
        y: completedCrop.y * scaleY,
        width: completedCrop.width * scaleX,
        height: completedCrop.height * scaleY,
      };

      onConfirm(croppedBlob, cropArea);
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    setCrop({
      unit: '%',
      width: 80,
      height: 80,
      x: 10,
      y: 10,
    });
    setCompletedCrop(null);
    onCancel();
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Drag corners to resize, drag inside to move. Crop box is fully adjustable.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Crop Area */}
          <div className="relative w-full rounded-md overflow-hidden bg-black">
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              className="max-h-96"
            >
              <img
                ref={imgRef}
                key={imageUrl + (initialCropArea ? JSON.stringify(initialCropArea) : '')}
                src={imageUrl}
                alt="Crop preview"
                className="max-w-full"
                crossOrigin="anonymous"
              />
            </ReactCrop>
          </div>

          {/* Controls */}
          <div className="flex justify-center">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleUseFullImage}
              disabled={isProcessing}
            >
              Use Full Image
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isProcessing}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isProcessing || !completedCrop}>
            {isProcessing ? 'Processing...' : 'Confirm Crop'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
